import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import Loading from "../Components/global/Loading";
import { useDispatch, useSelector } from "react-redux";
import { getAllPricingsList } from "../redux/actions/contents";
import PageName from "../Components/global/PageName";
import MyButton from "../Components/global/MyButton";
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheckCircle,
  FaStar,
  FaTimes,
  FaTag,
  FaSearch,
  FaUndo,
} from "react-icons/fa";
import { MdOutlineLocalOffer, MdOutlineCheck } from "react-icons/md";
import { uploadMediaToServer } from "../utils/uploadMediaToServer";

const defaultForm = {
  title: "",
  finish: "",
  bestFor: "",
  price: "",
  priceFormatted: "",
  icon: "star",
  image: "",
  description: "",
  features: [],
  popular: false,
};

export default function PricingManager() {
  const dispatch = useDispatch();
  const { pricingsList, pricingsCount, loading: pricingsLoading } = useSelector(
    (state) => state?.content
  );

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sync, setSync] = useState(0);
  const [search, setSearch] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [featureInput, setFeatureInput] = useState("");

  useEffect(() => {
    dispatch(getAllPricingsList(pageNo, pageSize, search));
  }, [sync, search, pageNo, pageSize, dispatch]);

  const totalCount = pricingsCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const startItem = totalCount > 0 ? (pageNo - 1) * pageSize + 1 : 0;
  const endItem = Math.min(pageNo * pageSize, totalCount);

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      const url = await uploadMediaToServer(file, "null");
      if (url) {
        setFormData((prev) => ({ ...prev, image: url }));
        toast.success("Image uploaded successfully");
      }
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingId(null);
    setFormData(defaultForm);
    setFeatureInput("");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title || "",
      finish: item.finish || "",
      bestFor: item.bestFor || "",
      price: item.price !== null && item.price !== undefined ? item.price : "",
      priceFormatted: item.priceFormatted || "",
      icon: item.icon || "star",
      image: item.image || "",
      description: item.description || "",
      features: Array.isArray(item.features) ? [...item.features] : [],
      popular: !!item.popular,
    });
    setFeatureInput("");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData(defaultForm);
    setFeatureInput("");
  };

  const handleAddFeature = () => {
    const trimmed = featureInput.trim();
    if (!trimmed) return;
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, trimmed],
    }));
    setFeatureInput("");
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      return toast.error("Title is required");
    }

    try {
      setLoading(true);
      const payload = {
        id: editingId ? Number(editingId) : 0,
        title: formData.title.trim(),
        finish: formData.finish.trim(),
        bestFor: formData.bestFor.trim(),
        price: formData.price !== "" ? Number(formData.price) : 0,
        priceFormatted: formData.priceFormatted.trim(),
        icon: formData.icon.trim(),
        image: formData.image.trim(),
        description: formData.description.trim(),
        features: formData.features,
        popular: formData.popular,
        deleted: false,
      };

      const token = localStorage.getItem("token");

      if (editingId) {
        // PUT /api/v1/pricing/updateById?id={editingId}
        const { data: res } = await axios.put(
          `${server}/pricing/updateById`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            params: { id: editingId },
          }
        );
        toast.success(res?.message || "Pricing updated successfully");
      } else {
        // POST /api/v1/pricing/new
        const { data: res } = await axios.post(
          `${server}/pricing/new`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        toast.success(res?.message || "Pricing saved successfully");
      }

      handleCloseModal();
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this pricing plan?"
    );
    if (!confirmDelete) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const { data: res } = await axios.delete(
        `${server}/pricing/deleteById`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { id },
        }
      );
      toast.success(res?.message || "Pricing deleted successfully");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async (id) => {
    const confirmRecover = window.confirm(
      "Are you sure you want to recover this deleted pricing plan?"
    );
    if (!confirmRecover) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const { data: res } = await axios.post(
        `${server}/pricing/recoverDeletedById`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { id },
        }
      );
      toast.success(res?.message || "Pricing plan recovered successfully");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Recovery failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-12">
      <PageName
        title="Manage Pricing Plans"
        subTitle="Create, update, recover, and organize pricing packages & offerings."
      />

      {/* Control Bar */}
      <div className="max-w-6xl mx-auto mt-6 px-4 flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search packages..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPageNo(1);
            }}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 bg-white shadow-sm"
          />
        </div>

        {/* Add New Button */}
        <MyButton
          title={
            <span className="flex items-center gap-2">
              <FaPlus /> Add New Pricing Package
            </span>
          }
          onClick={handleOpenCreateModal}
          variant="contained"
        />
      </div>

      {/* Pricing Cards Grid */}
      <div className="max-w-6xl mx-auto mt-8 px-4 flex flex-wrap justify-center gap-6">
        {pricingsList && pricingsList.length > 0 ? (
          pricingsList.map((item, index) => (
            <motion.div
              key={item.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className={`relative flex flex-col justify-between bg-white rounded-2xl p-6 shadow-md border transition-all duration-300 hover:shadow-xl w-full max-w-sm sm:w-[calc(50%-16px)] lg:w-[calc(33.333%-16px)] min-w-[280px] ${
                item.deleted
                  ? "opacity-60 border-rose-300 bg-rose-50/20"
                  : item.popular
                  ? "border-amber-400 ring-2 ring-amber-400/30"
                  : "border-gray-200"
              }`}
            >
              {/* Popular Badge */}
              {item.popular && !item.deleted && (
                <div className="absolute -top-3 right-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow flex items-center gap-1">
                  <FaStar className="text-yellow-200" /> Most Popular
                </div>
              )}

              {/* Deleted Badge */}
              {item.deleted && (
                <div className="absolute -top-3 left-4 bg-rose-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow flex items-center gap-1">
                  Deleted
                </div>
              )}

              <div>
                {/* Image Banner */}
                {item.image && (
                  <div className="mb-3 h-40 w-full overflow-hidden rounded-xl bg-gray-100">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                )}

                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      {item.title}
                    </h3>
                    {item.finish && (
                      <span className="inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                        {item.finish}
                      </span>
                    )}
                  </div>
                </div>

                {/* Price Display */}
                <div className="my-4">
                  <span className="text-3xl font-extrabold text-gray-900">
                    {item.priceFormatted || `₹${item.price ?? 0}`}
                  </span>
                </div>

                {/* Description */}
                {item.description && (
                  <p className="mb-3 text-xs text-gray-600 line-clamp-2 leading-relaxed">
                    {item.description}
                  </p>
                )}

                {/* Best For */}
                {item.bestFor && (
                  <div className="mb-4 text-xs text-gray-500 flex items-center gap-1.5 bg-gray-50 p-2 rounded-lg">
                    <MdOutlineLocalOffer className="text-slate-600 text-sm flex-shrink-0" />
                    <span>
                      <strong className="text-gray-700">Best for:</strong>{" "}
                      {item.bestFor}
                    </span>
                  </div>
                )}

                {/* Features List */}
                {Array.isArray(item.features) && item.features.length > 0 && (
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Package Features
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600">
                      {item.features.map((feat, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <MdOutlineCheck className="text-emerald-500 text-lg flex-shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={() => handleOpenEditModal(item)}
                  className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <FaEdit className="text-slate-600" /> Edit
                </button>

                {item.deleted ? (
                  <button
                    onClick={() => handleRecover(item.id)}
                    className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <FaUndo className="text-emerald-600" /> Recover
                  </button>
                ) : (
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="px-3 py-1.5 text-xs font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <FaTrash className="text-rose-600" /> Delete
                  </button>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full py-16 text-center text-gray-400 bg-white rounded-2xl border border-dashed border-gray-300 w-full">
            <FaTag className="mx-auto text-4xl mb-2 text-gray-300" />
            <p className="text-base font-medium">No Pricing Plans Found</p>
            <p className="text-xs text-gray-400 mt-1">
              Click &quot;Add New Pricing Package&quot; to create your first package.
            </p>
          </div>
        )}
      </div>

      {/* Pagination Bar */}
      {totalCount > 0 && (
        <div className="max-w-6xl mx-auto mt-8 px-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="text-xs text-gray-500 font-medium">
            Showing <span className="font-semibold text-gray-800">{startItem}</span> to{" "}
            <span className="font-semibold text-gray-800">{endItem}</span> of{" "}
            <span className="font-semibold text-gray-800">{totalCount}</span> pricing plans
          </div>

          <div className="flex items-center gap-4">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">Per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageNo(1);
                }}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-slate-500 font-medium text-gray-700"
              >
                <option value={2}>2</option>
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={50}>50</option>
              </select>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-1">
              <button
                disabled={pageNo <= 1}
                onClick={() => setPageNo((prev) => Math.max(1, prev - 1))}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>

              <div className="flex items-center gap-1 px-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    onClick={() => setPageNo(num)}
                    className={`w-7 h-7 text-xs font-bold rounded-lg transition-colors ${
                      pageNo === num
                        ? "bg-slate-900 text-white shadow-sm"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>

              <button
                disabled={pageNo >= totalPages}
                onClick={() => setPageNo((prev) => Math.min(totalPages, prev + 1))}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialog for Add / Edit */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-slate-900 text-white">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  {editingId ? <FaEdit /> : <FaPlus />}
                  {editingId ? "Edit Pricing Package" : "Add New Pricing Package"}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FaTimes size={18} />
                </button>
              </div>

              {/* Modal Body / Form */}
              <form
                onSubmit={handleSubmit}
                className="p-6 overflow-y-auto space-y-4 flex-1"
              >
                {/* Title & Finish */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Package Title <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Standard Album Package"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Finish Type
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Glossy Finish / HD Velvet"
                      value={formData.finish}
                      onChange={(e) =>
                        setFormData({ ...formData, finish: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  </div>
                </div>

                {/* Best For & Price */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Best For
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Weddings & Events"
                      value={formData.bestFor}
                      onChange={(e) =>
                        setFormData({ ...formData, bestFor: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Numerical Price (Numeric)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 25000"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Formatted Price (Display)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. ₹25,000 / event"
                      value={formData.priceFormatted}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          priceFormatted: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  </div>
                </div>

                {/* Image Upload & Preview */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Package Banner Image
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileChange}
                      className="block w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                    />
                    {uploadingImage && (
                      <span className="text-xs text-slate-500 animate-pulse font-medium">Uploading...</span>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Or enter Image URL (http://...)"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="w-full mt-2 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                  {formData.image && (
                    <div className="mt-2 relative w-32 h-20 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                      <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Package Description
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Enter detailed description for this service package..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                </div>

                {/* Icon & Popular */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Icon (Name or Tag)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. star, crown, gem"
                      value={formData.icon}
                      onChange={(e) =>
                        setFormData({ ...formData, icon: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  </div>
                  <div className="pt-4">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={formData.popular}
                        onChange={(e) =>
                          setFormData({ ...formData, popular: e.target.checked })
                        }
                        className="w-4 h-4 text-slate-800 rounded border-gray-300 focus:ring-slate-500"
                      />
                      <span className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                        <FaStar className="text-amber-500" /> Mark as Most Popular Package
                      </span>
                    </label>
                  </div>
                </div>

                {/* Features Dynamic List */}
                <div className="pt-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Package Features List
                  </label>

                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="e.g. 50 Page Premium Photobook"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddFeature();
                        }
                      }}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddFeature}
                      className="px-4 py-2 bg-slate-800 text-white text-xs font-semibold rounded-xl hover:bg-slate-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>

                  {/* Render Features Tags */}
                  <div className="space-y-1.5 max-h-36 overflow-y-auto p-2 bg-gray-50 rounded-xl border border-gray-200">
                    {formData.features && formData.features.length > 0 ? (
                      formData.features.map((feat, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 shadow-sm"
                        >
                          <span className="flex items-center gap-1.5">
                            <FaCheckCircle className="text-emerald-500" />
                            {feat}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(idx)}
                            className="text-gray-400 hover:text-rose-600 transition-colors"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-gray-400 italic text-center py-2">
                        No features added yet. Type feature above and click Add.
                      </p>
                    )}
                  </div>
                </div>

                {/* Form Action Footer */}
                <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <MyButton
                    type="submit"
                    title={editingId ? "Update Pricing" : "Create Pricing"}
                    loading={loading}
                  />
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {(loading || pricingsLoading) && <Loading />}
    </div>
  );
}
